import { ComponentProps, FC } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { useTranslate } from "@/hooks/useTranslate";
import { ChildrenOrI18nType } from "@/utils/react";
import { cn } from "@/utils/tailwind";

import { RmText } from "./RmText/RmText";
import { textVariants } from "./RmText/variants";

export const RmDialog: FC<
  ComponentProps<typeof DialogPrimitive.Root>
> = props => {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
};

export const RmDialogTrigger: FC<
  ComponentProps<typeof DialogPrimitive.Trigger>
> = props => {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
};

export const RmDialogPortal: FC<
  ComponentProps<typeof DialogPrimitive.Portal>
> = props => {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
};

export const RmDialogClose: FC<
  ComponentProps<typeof DialogPrimitive.Close>
> = props => {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
};

export const RmDialogOverlay: FC<
  ComponentProps<typeof DialogPrimitive.Overlay>
> = ({ className, ...props }) => {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
};

export const RmDialogContent: FC<
  ComponentProps<typeof DialogPrimitive.Content>
> = ({ className, children, onPointerDownOutside, ...props }) => {
  return (
    <RmDialogPortal data-slot="dialog-portal">
      <RmDialogOverlay>
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            "fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-background p-6 shadow-lg duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:max-w-lg",
            className,
          )}
          onPointerDownOutside={e => {
            if (
              e.target instanceof Element &&
              e.target.closest("[data-sonner-toast]")
            ) {
              e.preventDefault();
            }

            onPointerDownOutside?.(e);
          }}
          {...props}>
          {children}
          <DialogPrimitive.Close className="absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring/50 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
            <XIcon />
            <RmText as="span" className="sr-only" i18nKey="common:close" />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </RmDialogOverlay>
    </RmDialogPortal>
  );
};

export const RmDialogHeader: FC<ComponentProps<"div">> = ({
  className,
  ...props
}) => {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
};

export const RmDialogFooter: FC<ComponentProps<"div">> = ({
  className,
  ...props
}) => {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
};

type RmDialogTitlePropsType = ComponentProps<typeof DialogPrimitive.Title> &
  ChildrenOrI18nType;

export const RmDialogTitle: FC<RmDialogTitlePropsType> = ({
  className,
  i18nKey,
  i18nOptions,
  children,
  ...props
}) => {
  const i18nText = useTranslate(i18nKey, i18nOptions);
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        textVariants({
          variant: "headerTwo",
        }),
        className,
      )}
      {...props}>
      {children ?? i18nText}
    </DialogPrimitive.Title>
  );
};

type RmDialogDescriptionPropsType = ComponentProps<
  typeof DialogPrimitive.Description
> &
  ChildrenOrI18nType;

export const RmDialogDescription: FC<RmDialogDescriptionPropsType> = ({
  className,
  children,
  i18nKey,
  i18nOptions,
  ...props
}) => {
  const i18nText = useTranslate(i18nKey, i18nOptions);

  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground", textVariants(), className)}
      {...props}>
      {children ?? i18nText}
    </DialogPrimitive.Description>
  );
};
